import "./PaymentDetails.css";
import { useState, useEffect } from "react";
import PaymentPanel, { PaymentMethod } from "./PaymentPanel"; // Import PaymentMethod type
import PaymentsListPanel, { PaymentItem, PaymentState } from "./PaymentsListPanel";
import SimpleButton from "../../buttons/Simple-button";

// Add necessary imports
import { useForm, FormProvider } from "react-hook-form";
import { v4 as uuidv4 } from 'uuid';
import apiHelper from "../../../utils/apiHelper";
import DateHelper from "../../../utils/DateHelper";
import { PaymentService } from "@propertelligent/client-api";
import {
  usePaymentSubmitGuard,
  PayPaymentDueAccountWithKey,
  NextIdempotencyKey,
} from "../../../hooks/usePaymentSubmitGuard";

// Define the shape of the form data
type FormValues = {
  mode: string;
  date: string;
  checkNo?: string;
  transactionId?: string;
};

export default function Page() {
  const [loadedPayments, setLoadedPayments] = useState<PaymentItem[]>([]);
  const [paymentStates, setPaymentStates] = useState<PaymentState[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]); // State to hold methods from child
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for submission
  const { isSubmitting: isPaySubmitting, runGuarded: runPayGuarded } =
    usePaymentSubmitGuard();

  // Initialize react-hook-form
  const methods = useForm<FormValues>({
    defaultValues: {
      mode: "",
      date: DateHelper.getTodayLocalDateString(), // Set a sensible default
      checkNo: "",
      transactionId: "",
    },
  });

  // Initialize payment states when loadedPayments changes
  useEffect(() => {
    setPaymentStates(
      loadedPayments.map((payment, index) => ({
        checked: index === 0,
        portion: index === 0 ? "full" : null,
        amount: index === 0 ? payment.total / 2 : 0,
      }))
    );
  }, [loadedPayments]);

  const calcRow = (state: PaymentState, total: number) =>
    state.checked
      ? state.portion === "partial"
        ? Math.max(0, Math.min(total, Number.isFinite(state.amount) ? state.amount : 0))
        : state.portion === "full"
        ? total
        : 0
      : 0;

  const paidTotal = paymentStates.reduce(
    (sum, state, index) => sum + calcRow(state, loadedPayments[index]?.total || 0),
    0
  );

  // API Submission Logic
  const postPayments = async (
    formData: FormValues,
    nextKey: NextIdempotencyKey,
  ) => {
    setIsSubmitting(true);

    const selectedMethod = paymentMethods.find(m => m.Id === formData.mode);
    if (!selectedMethod) {
      console.error("Payment method not selected or found.");
      setIsSubmitting(false);
      return;
    }

    const paymentsToProcess = loadedPayments
      .map((payment, index) => ({
        payment,
        state: paymentStates[index],
      }))
      .filter(({ state }) => state && state.checked);

    if (paymentsToProcess.length === 0) {
      console.warn("No payments selected.");
      setIsSubmitting(false);
      return;
    }

    const paymentPromises = paymentsToProcess.map(({ payment, state }) => {
      const amountToPay = calcRow(state, payment.total);

      let transactionId = formData.transactionId || null;
      if (selectedMethod.PaymentMethodTypeId === 'Cash' || selectedMethod.PaymentMethodTypeId === 'Check') {
        transactionId = uuidv4();
      }

      const payload: PayPaymentDueAccountWithKey = {
        payDueAccountId: parseInt(payment.id),
        amount: amountToPay,
        fromPaymentMethodId: payment.fromPaymentMethodId,
        checkNumber: formData.checkNo || "",
        paymentDate: new Date(formData.date).toISOString(),
        transactionId: transactionId,
        idempotencyKey: nextKey(),
      };

      return apiHelper(
        PaymentService.putApiPaymentDueAccountPay,
        {
          successMessage: `Payment for '${payment.title}' posted successfully!`,
          failureMessage: `Failed to post payment for '${payment.title}'.`,
          showNotification: true,
        },
        payload
      );
    });

    try {
      await Promise.all(paymentPromises);
      methods.reset();
      // Optionally, add logic to refetch the payments list here
    } catch (error) {
      console.error("One or more payments failed to post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePostPayment = (formData: FormValues) =>
    runPayGuarded((nextKey) => postPayments(formData, nextKey));

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handlePostPayment)}>
        <main className="payment-page" role="main" aria-label="Payments">
          <div className="page-container">
            <PaymentsListPanel
              payments={loadedPayments}
              onPaymentChange={setPaymentStates}
              payeeAccountId={1428}
              setLoadedPayments={setLoadedPayments}
            />
            <PaymentPanel
              paidAmount={paidTotal}
              defaultDate="12/02/2028"
              setPaymentMethods={setPaymentMethods} // Pass setter to lift state up
            />
          </div>

          <footer className="page-footer" role="contentinfo">
            <button className="link-btn" type="button">
              Cancel
            </button>
            <SimpleButton
              label={isSubmitting || isPaySubmitting ? "Posting..." : "Post Payment"}
              type="submit" // Use "submit" to trigger the form
              isDisabled={isSubmitting || isPaySubmitting}
              className="mx-2"
            />
          </footer>
        </main>
      </form>
    </FormProvider>
  );
}